import { ChangeDetectionStrategy, Component, computed, DestroyRef, input, output, OnChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NumberInputComponent } from '@examdojo/core/input';
import { SingleSelectComponent } from '@examdojo/core/select';
import { THEME_COLORS_WITH_SHADES } from '../../elements/theme';
import type { StylePropertySchema } from './style-property-schema.model';

interface SelectOption {
  label: string;
  value: string;
}

/**
 * Renders a single style property control based on its schema.
 * Supports: color (select), number (input), toggle (slide), select (dropdown).
 */
@Component({
  selector: 'dojo-style-property-editor',
  standalone: true,
  imports: [ReactiveFormsModule, MatSlideToggleModule, NumberInputComponent, SingleSelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (schema().control) {
      @case ('color') {
        <dojo-single-select
          [label]="schema().label"
          [formCtrl]="selectCtrl"
          [options]="colorOptions"
          optionLabel="label"
          optionValue="value"
          [size]="'s'"
          [placeholder]="colorPlaceholder()"
          (selectionChange)="onSelectChange($event)"
        />
      }
      @case ('number') {
        <dojo-number-input
          [label]="schema().label"
          [formCtrl]="numberCtrl"
          [step]="schema().step ?? 1"
          [min]="schema().min ?? 0"
          [max]="schema().max ?? 100"
          [size]="'s'"
          [placeholder]="numberPlaceholder()"
        />
      }
      @case ('toggle') {
        <mat-slide-toggle [checked]="toggleChecked()" (change)="valueChange.emit($event.checked)">{{
          schema().label
        }}</mat-slide-toggle>
      }
      @case ('select') {
        @if (schema().options) {
          <dojo-single-select
            [label]="schema().label"
            [formCtrl]="selectCtrl"
            [options]="getSelectOptions()"
            optionLabel="label"
            optionValue="value"
            [size]="'s'"
            (selectionChange)="onSelectChange($event)"
          />
        }
      }
    }
  `,
})
export class StylePropertyEditorComponent implements OnChanges {
  constructor(private readonly destroyRef: DestroyRef) {}

  readonly schema = input.required<StylePropertySchema>();
  readonly value = input<unknown>();
  readonly defaultValue = input<unknown>();
  readonly valueChange = output<unknown>();

  readonly colorPlaceholder = computed(() => {
    const dv = this.defaultValue();
    return dv ? `Default (${String(dv)})` : 'Default';
  });

  readonly numberPlaceholder = computed(() => {
    const dv = this.defaultValue();
    return dv !== null ? String(dv) : '';
  });

  readonly toggleChecked = computed(() => {
    const v = this.value();
    if (v !== null) {
      return !!v;
    }
    const dv = this.defaultValue();
    return dv !== null ? !!dv : false;
  });

  readonly colorOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    ...THEME_COLORS_WITH_SHADES.map((c) => ({ label: c, value: c })),
  ];

  readonly selectCtrl = new FormControl<string>('', { nonNullable: true });
  readonly numberCtrl = new FormControl<number | null>(null);

  private selectSub?: { unsubscribe(): void };
  private numberSub?: { unsubscribe(): void };

  ngOnChanges(): void {
    const v = this.value();
    const s = this.schema();

    if (s.control === 'color' || s.control === 'select') {
      this.selectCtrl.setValue((v as string) ?? '', { emitEvent: false });
      if (!this.selectSub) {
        this.selectSub = this.selectCtrl.valueChanges
          .pipe(
            tap((val) => this.valueChange.emit(val || undefined)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      }
    } else if (s.control === 'number') {
      this.numberCtrl.setValue((v as number) ?? null, { emitEvent: false });
      if (!this.numberSub) {
        this.numberSub = this.numberCtrl.valueChanges
          .pipe(
            tap((val) => this.valueChange.emit(val)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();
      }
    }
  }

  onSelectChange(value: unknown): void {
    this.valueChange.emit(value || undefined);
  }

  getSelectOptions(): SelectOption[] {
    const opts = this.schema().options;
    if (!opts) {
      return [];
    }
    return opts as SelectOption[];
  }
}
